import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildQuoteMessage, waLink } from "@/lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_KEYS = ["s", "m", "l", "xl"];
const ARTIST_KEYS = ["any", "eeme", "may", "tyson", "alvaro", "avelino", "ezzer"];

export const QuoteForm = ({ t, lang }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_type: "tattoo",
    style: "",
    size: "",
    placement: "",
    preferred_artist: "any",
    when: "exploring",
    when_date: "",
    name: "",
    language: lang,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error(lang === "es" ? "Por favor escribe tu nombre" : "Please enter your name");
      return;
    }
    setSubmitting(true);
    try {
      // store quote in backend
      const payload = { ...form, language: lang };
      await axios.post(`${BACKEND_URL}/api/quotes`, payload);
      const message = buildQuoteMessage(payload, lang, BACKEND_URL);
      toast.success(t.quote.success);
      window.open(waLink(message), "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      // Fall back: still open WhatsApp even if backend fails
      const message = buildQuoteMessage(form, lang, BACKEND_URL);
      window.open(waLink(message), "_blank", "noopener,noreferrer");
      toast.error(t.quote.error);
    } finally {
      setSubmitting(false);
    }
  };

  const isTattoo = form.service_type === "tattoo";

  return (
    <section
      id="quote"
      className="relative py-14 md:py-20 bg-bendito-bg"
      data-testid="quote-section"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="mb-10 reveal">
          <div className="eyebrow mb-3 text-bendito-orange">{t.quote.eyebrow}</div>
          <h2 className="font-heading text-bendito-ink text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight">
            <span className="ink-underline">{t.quote.title}</span>
          </h2>
          <p className="text-bendito-muted mt-3 max-w-2xl">{t.quote.sub}</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-bendito-surface border border-bendito-border p-6 md:p-10 space-y-9"
          data-testid="quote-form"
        >
          {/* Service type */}
          <div>
            <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
              {t.quote.service}
            </Label>
            <RadioGroup
              value={form.service_type}
              onValueChange={(v) => update("service_type", v)}
              className="grid grid-cols-3 gap-2 mt-3"
              data-testid="quote-service"
            >
              {[
                ["tattoo", t.quote.services.tattoo],
                ["piercing", t.quote.services.piercing],
                ["body_mod", t.quote.services.bodyMod],
              ].map(([val, label]) => (
                <label
                  key={val}
                  htmlFor={`svc-${val}`}
                  className={`flex items-center justify-center px-3 py-3 border cursor-pointer font-heading tracking-wider text-sm uppercase transition-colors ${
                    form.service_type === val
                      ? "bg-bendito-orange text-black border-bendito-orange"
                      : "border-bendito-border text-bendito-ink hover:border-bendito-ink"
                  }`}
                  data-testid={`quote-service-${val}`}
                >
                  <RadioGroupItem id={`svc-${val}`} value={val} className="sr-only" />
                  {label}
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Style (tattoo only) */}
          {isTattoo && (
            <div>
              <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
                {t.quote.style}
              </Label>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="quote-style">
                {t.quote.styles.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => update("style", s)}
                    className={`px-4 py-2 border font-mono text-xs tracking-[0.2em] uppercase transition-colors ${
                      form.style === s
                        ? "bg-bendito-orange text-black border-bendito-orange"
                        : "border-bendito-border text-bendito-muted hover:text-bendito-ink hover:border-bendito-ink"
                    }`}
                    data-testid={`quote-style-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          <div>
            <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
              {t.quote.size}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="quote-size">
              {SIZE_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => update("size", t.quote.sizes[k])}
                  className={`text-left px-4 py-3 border transition-colors text-sm ${
                    form.size === t.quote.sizes[k]
                      ? "bg-bendito-orange text-black border-bendito-orange"
                      : "border-bendito-border text-bendito-ink/90 hover:border-bendito-ink"
                  }`}
                  data-testid={`quote-size-${k}`}
                >
                  {t.quote.sizes[k]}
                </button>
              ))}
            </div>
          </div>

          {/* Placement */}
          <div>
            <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
              {t.quote.placement}
            </Label>
            <Select value={form.placement} onValueChange={(v) => update("placement", v)}>
              <SelectTrigger
                className="mt-3 bg-transparent border-bendito-border rounded-none text-bendito-ink h-12"
                data-testid="quote-placement-trigger"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-bendito-surface border-bendito-border rounded-none">
                {t.quote.placements.map((p) => (
                  <SelectItem key={p} value={p} className="text-bendito-ink rounded-none">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Artist + When grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
                {t.quote.artist}
              </Label>
              <Select
                value={form.preferred_artist}
                onValueChange={(v) => update("preferred_artist", v)}
              >
                <SelectTrigger
                  className="mt-3 bg-transparent border-bendito-border rounded-none text-bendito-ink h-12"
                  data-testid="quote-artist-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-bendito-surface border-bendito-border rounded-none">
                  {ARTIST_KEYS.map((k) => (
                    <SelectItem key={k} value={k} className="text-bendito-ink rounded-none">
                      {t.quote.artistOptions[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted">
                {t.quote.when}
              </Label>
              <RadioGroup
                value={form.when}
                onValueChange={(v) => update("when", v)}
                className="mt-3 space-y-2"
                data-testid="quote-when"
              >
                {[
                  ["now", t.quote.whenOptions.now],
                  ["arriving", t.quote.whenOptions.arriving],
                  ["exploring", t.quote.whenOptions.exploring],
                ].map(([val, label]) => (
                  <label
                    key={val}
                    htmlFor={`when-${val}`}
                    className="flex items-center gap-3 cursor-pointer text-sm text-bendito-ink/90"
                  >
                    <RadioGroupItem
                      id={`when-${val}`}
                      value={val}
                      className="border-bendito-border data-[state=checked]:bg-bendito-orange data-[state=checked]:border-bendito-orange"
                    />
                    {label}
                  </label>
                ))}
              </RadioGroup>
              {form.when === "arriving" && (
                <Input
                  type="date"
                  value={form.when_date}
                  onChange={(e) => update("when_date", e.target.value)}
                  className="mt-3 bg-transparent border-bendito-border rounded-none text-bendito-ink h-12"
                  data-testid="quote-when-date"
                />
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <Label
              htmlFor="quote-name"
              className="font-mono text-[11px] tracking-[0.28em] uppercase text-bendito-muted"
            >
              {t.quote.name}
            </Label>
            <Input
              id="quote-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="mt-3 bg-transparent border-0 border-b border-bendito-border rounded-none text-bendito-ink h-12 focus:border-bendito-orange"
              placeholder={lang === "es" ? "Cómo te llamas?" : "What's your name?"}
              data-testid="quote-name-input"
            />
          </div>

          <div className="pt-2 flex flex-col gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-bendito-orange hover:bg-bendito-orangeDark text-black font-heading tracking-[0.25em] uppercase text-base px-8 py-4 transition-colors disabled:opacity-60"
              data-testid="quote-submit-btn"
            >
              {submitting ? t.quote.sending : t.quote.submit}
            </button>
            <p className="text-xs text-bendito-muted text-center font-mono tracking-wide">
              {t.quote.note}
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;
